import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { DataService } from '../services/data-service';
import {
  ApiResponse,
  DashboardStats,
  InventoryByProperty,
  StaleInventoryData,
  RecentActivitiesData,
  WarehouseUtilizationData,
  TopStorageUnitsData,
  PropertySummaryData,
  LowStockItemsData,
  ExcessStockItemsData,
  ItemVelocityData,
  ShipmentsData,
  TransferOrdersData,
  DefaultWarehouseStatus
} from '../types/dashboard.types';

// Query keys for cache management
export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
  inventoryByProperty: (property: string) =>
    [...dashboardKeys.all, 'inventory', property] as const,
  staleInventory: (days: number, pageNumber: number, pageSize: number, searchTerm: string) =>
    [...dashboardKeys.all, 'stale', days, pageNumber, pageSize, searchTerm] as const,
  recentActivity: (limit: number) =>
    [...dashboardKeys.all, 'activity', limit] as const,
  warehouseUtilization: () =>
    [...dashboardKeys.all, 'warehouse-utilization'] as const,
  topStorageUnits: (limit: number) =>
    [...dashboardKeys.all, 'storage-units', limit] as const,
  propertySummary: () => [...dashboardKeys.all, 'property-summary'] as const,
  lowStockItems: (pageNumber: number, pageSize: number, searchTerm: string) =>
    [...dashboardKeys.all, 'low-stock', pageNumber, pageSize, searchTerm] as const,
  excessStockItems: (pageNumber: number, pageSize: number, searchTerm: string) =>
    [...dashboardKeys.all, 'excess-stock', pageNumber, pageSize, searchTerm] as const,
  itemVelocity: (days: number, pageNumber: number, pageSize: number) =>
    [...dashboardKeys.all, 'velocity', days, pageNumber, pageSize] as const,
  overdueShipments: (pageNumber: number, pageSize: number) =>
    [...dashboardKeys.all, 'overdue-shipments', pageNumber, pageSize] as const,
  recentShipments: (daysAhead: number, pageNumber: number, pageSize: number) =>
    [...dashboardKeys.all, 'recent-shipments', daysAhead, pageNumber, pageSize] as const,
  arrivedShipments: (pageNumber: number, pageSize: number) =>
    [...dashboardKeys.all, 'arrived-shipments', pageNumber, pageSize] as const,
  activeTransferOrders: (pageNumber: number, pageSize: number) =>
    [...dashboardKeys.all, 'active-transfer-orders', pageNumber, pageSize] as const,
  defaultWarehouseStatus: () =>
    [...dashboardKeys.all, 'default-warehouse-status'] as const
};

// Fetch functions
const fetchDashboardStats = async (): Promise<DashboardStats> => {
  const response = await DataService.get('/api/Dashboard/stats');
  if (!response.ok) throw new Error('Failed to fetch dashboard stats');
  const data: ApiResponse<DashboardStats> = await response.json();
  return data.data;
};

const fetchInventoryByProperty = async (
  propertyName: string
): Promise<InventoryByProperty> => {
  const response = await DataService.get(
    `/api/Dashboard/inventory-by-property?propertyName=${propertyName}`
  );
  if (!response.ok) throw new Error('Failed to fetch inventory by property');
  const data: ApiResponse<InventoryByProperty> = await response.json();
  return data.data;
};

const fetchStaleInventory = async (
  days: number,
  pageNumber: number,
  pageSize: number,
  searchTerm: string
): Promise<StaleInventoryData> => {
  let url = `/api/Dashboard/stale-inventory?days=${days}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
  if (searchTerm) {
    url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  }
  const response = await DataService.get(url);
  if (!response.ok) throw new Error('Failed to fetch stale inventory');
  const data: ApiResponse<StaleInventoryData> = await response.json();
  return data.data;
};

const fetchRecentActivity = async (
  limit: number
): Promise<RecentActivitiesData> => {
  const response = await DataService.get(
    `/api/Dashboard/recent-activity?limit=${5}`
  );
  if (!response.ok) throw new Error('Failed to fetch recent activity');
  const data: ApiResponse<RecentActivitiesData> = await response.json();
  return data.data;
};

const fetchWarehouseUtilization =
  async (): Promise<WarehouseUtilizationData> => {
    const response = await DataService.get('/api/Dashboard/warehouse-utilization');
    if (!response.ok)
      throw new Error('Failed to fetch warehouse utilization');
    const data: ApiResponse<WarehouseUtilizationData> = await response.json();
    return data.data;
  };

const fetchTopStorageUnits = async (
  limit: number
): Promise<TopStorageUnitsData> => {
  const response = await DataService.get(
    `/api/Dashboard/top-storage-units?limit=${limit}`
  );
  if (!response.ok) throw new Error('Failed to fetch top storage units');
  const data: ApiResponse<TopStorageUnitsData> = await response.json();
  return data.data;
};

const fetchPropertySummary = async (): Promise<PropertySummaryData> => {
  const response = await DataService.get('/api/Dashboard/property-summary');
  if (!response.ok) throw new Error('Failed to fetch property summary');
  const data: ApiResponse<PropertySummaryData> = await response.json();
  return data.data;
};

// Custom Hooks (React Query v4 compatible - no gcTime, uses cacheTime instead)
export const useDashboardStats = (): UseQueryResult<DashboardStats, Error> => {
  return useQuery({
    queryKey: dashboardKeys.stats(),
    queryFn: fetchDashboardStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000 // 10 minutes (gcTime in v5)
  });
};

export const useInventoryByProperty = (
  propertyName: string
): UseQueryResult<InventoryByProperty, Error> => {
  return useQuery({
    queryKey: dashboardKeys.inventoryByProperty(propertyName),
    queryFn: () => fetchInventoryByProperty(propertyName),
    enabled: !!propertyName,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });
};

export const useStaleInventory = (
  days: number = 30,
  pageNumber: number = 1,
  pageSize: number = 20,
  searchTerm: string = ''
): UseQueryResult<StaleInventoryData, Error> => {
  return useQuery({
    queryKey: dashboardKeys.staleInventory(days, pageNumber, pageSize, searchTerm),
    queryFn: () => fetchStaleInventory(days, pageNumber, pageSize, searchTerm),
    staleTime: 3 * 60 * 1000, // 3 minutes
    cacheTime: 10 * 60 * 1000
  });
};

export const useRecentActivity = (
  limit: number = 10
): UseQueryResult<RecentActivitiesData, Error> => {
  return useQuery({
    queryKey: dashboardKeys.recentActivity(limit),
    queryFn: () => fetchRecentActivity(limit),
    staleTime: 1 * 60 * 1000, // 1 minute - recent data needs frequent updates
    cacheTime: 5 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000 // Auto-refetch every 2 minutes
  });
};

export const useWarehouseUtilization =
  (): UseQueryResult<WarehouseUtilizationData, Error> => {
    return useQuery({
      queryKey: dashboardKeys.warehouseUtilization(),
      queryFn: fetchWarehouseUtilization,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    });
  };

export const useTopStorageUnits = (
  limit: number = 3
): UseQueryResult<TopStorageUnitsData, Error> => {
  return useQuery({
    queryKey: dashboardKeys.topStorageUnits(limit),
    queryFn: () => fetchTopStorageUnits(limit),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });
};

export const usePropertySummary =
  (): UseQueryResult<PropertySummaryData, Error> => {
    return useQuery({
      queryKey: dashboardKeys.propertySummary(),
      queryFn: fetchPropertySummary,
      staleTime: 10 * 60 * 1000, // 10 minutes - property data changes less frequently
      cacheTime: 15 * 60 * 1000
    });
  };

// NEW API ENDPOINTS

// Low Stock Items
const fetchLowStockItems = async (
  pageNumber: number,
  pageSize: number,
  searchTerm: string
): Promise<LowStockItemsData> => {
  let url = `/api/Dashboard/low-stock-items?pageNumber=${pageNumber}&pageSize=${pageSize}`;
  if (searchTerm) {
    url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  }
  const response = await DataService.get(url);
  if (!response.ok) throw new Error('Failed to fetch low stock items');
  const data: ApiResponse<LowStockItemsData> = await response.json();
  return data.data;
};

export const useLowStockItems = (
  pageNumber: number = 1,
  pageSize: number = 10,
  searchTerm: string = ''
): UseQueryResult<LowStockItemsData, Error> => {
  return useQuery({
    queryKey: dashboardKeys.lowStockItems(pageNumber, pageSize, searchTerm),
    queryFn: () => fetchLowStockItems(pageNumber, pageSize, searchTerm),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });
};

// Excess Stock Items
const fetchExcessStockItems = async (
  pageNumber: number,
  pageSize: number,
  searchTerm: string
): Promise<ExcessStockItemsData> => {
  let url = `/api/Dashboard/excess-stock-items?pageNumber=${pageNumber}&pageSize=${pageSize}`;
  if (searchTerm) {
    url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  }
  const response = await DataService.get(url);
  if (!response.ok) throw new Error('Failed to fetch excess stock items');
  const data: ApiResponse<ExcessStockItemsData> = await response.json();
  return data.data;
};

export const useExcessStockItems = (
  pageNumber: number = 1,
  pageSize: number = 10,
  searchTerm: string = ''
): UseQueryResult<ExcessStockItemsData, Error> => {
  return useQuery({
    queryKey: dashboardKeys.excessStockItems(pageNumber, pageSize, searchTerm),
    queryFn: () => fetchExcessStockItems(pageNumber, pageSize, searchTerm),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });
};

// Item Velocity
const fetchItemVelocity = async (
  days: number,
  pageNumber: number,
  pageSize: number
): Promise<ItemVelocityData> => {
  const response = await DataService.get(
    `/api/Dashboard/item-velocity?days=${days}&pageNumber=${pageNumber}&pageSize=${pageSize}`
  );
  if (!response.ok) throw new Error('Failed to fetch item velocity');
  const data: ApiResponse<ItemVelocityData> = await response.json();
  return data.data;
};

export const useItemVelocity = (
  days: number = 30,
  pageNumber: number = 1,
  pageSize: number = 10
): UseQueryResult<ItemVelocityData, Error> => {
  return useQuery({
    queryKey: dashboardKeys.itemVelocity(days, pageNumber, pageSize),
    queryFn: () => fetchItemVelocity(days, pageNumber, pageSize),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });
};

// Overdue Shipments
const fetchOverdueShipments = async (
  pageNumber: number,
  pageSize: number
): Promise<ShipmentsData> => {
  const response = await DataService.get(
    `/api/Dashboard/overdue-shipments?pageNumber=${pageNumber}&pageSize=${pageSize}`
  );
  if (!response.ok) throw new Error('Failed to fetch overdue shipments');
  const data: ApiResponse<ShipmentsData> = await response.json();
  return data.data;
};

export const useOverdueShipments = (
  pageNumber: number = 1,
  pageSize: number = 10
): UseQueryResult<ShipmentsData, Error> => {
  return useQuery({
    queryKey: dashboardKeys.overdueShipments(pageNumber, pageSize),
    queryFn: () => fetchOverdueShipments(pageNumber, pageSize),
    staleTime: 2 * 60 * 1000, // 2 minutes - critical data
    cacheTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000 // Auto-refetch every 5 minutes
  });
};

// Recent Shipments
const fetchRecentShipments = async (
  daysAhead: number,
  pageNumber: number,
  pageSize: number
): Promise<ShipmentsData> => {
  const response = await DataService.get(
    `/api/Dashboard/recent-shipments?daysAhead=${daysAhead}&pageNumber=${pageNumber}&pageSize=${pageSize}`
  );
  if (!response.ok) throw new Error('Failed to fetch recent shipments');
  const data: ApiResponse<ShipmentsData> = await response.json();
  return data.data;
};

export const useRecentShipments = (
  daysAhead: number = 7,
  pageNumber: number = 1,
  pageSize: number = 10
): UseQueryResult<ShipmentsData, Error> => {
  return useQuery({
    queryKey: dashboardKeys.recentShipments(daysAhead, pageNumber, pageSize),
    queryFn: () => fetchRecentShipments(daysAhead, pageNumber, pageSize),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });
};

// Arrived Shipments
const fetchArrivedShipments = async (
  pageNumber: number,
  pageSize: number
): Promise<ShipmentsData> => {
  const response = await DataService.get(
    `/api/Dashboard/arrived-shipments?pageNumber=${pageNumber}&pageSize=${pageSize}`
  );
  if (!response.ok) throw new Error('Failed to fetch arrived shipments');
  const data: ApiResponse<ShipmentsData> = await response.json();
  return data.data;
};

export const useArrivedShipments = (
  pageNumber: number = 1,
  pageSize: number = 10
): UseQueryResult<ShipmentsData, Error> => {
  return useQuery({
    queryKey: dashboardKeys.arrivedShipments(pageNumber, pageSize),
    queryFn: () => fetchArrivedShipments(pageNumber, pageSize),
    staleTime: 3 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });
};

// Active Transfer Orders
const fetchActiveTransferOrders = async (
  pageNumber: number,
  pageSize: number
): Promise<TransferOrdersData> => {
  const response = await DataService.get(
    `/api/Dashboard/active-transfer-orders?pageNumber=${pageNumber}&pageSize=${pageSize}`
  );
  if (!response.ok) throw new Error('Failed to fetch active transfer orders');
  const data: ApiResponse<TransferOrdersData> = await response.json();
  return data.data;
};

export const useActiveTransferOrders = (
  pageNumber: number = 1,
  pageSize: number = 10
): UseQueryResult<TransferOrdersData, Error> => {
  return useQuery({
    queryKey: dashboardKeys.activeTransferOrders(pageNumber, pageSize),
    queryFn: () => fetchActiveTransferOrders(pageNumber, pageSize),
    staleTime: 2 * 60 * 1000, // 2 minutes - operational data
    cacheTime: 5 * 60 * 1000,
    refetchInterval: 3 * 60 * 1000 // Auto-refetch every 3 minutes
  });
};

// Default Warehouse Status
const fetchDefaultWarehouseStatus =
  async (): Promise<DefaultWarehouseStatus> => {
    const response = await DataService.get(
      '/api/Dashboard/default-warehouse-status'
    );
    if (!response.ok)
      throw new Error('Failed to fetch default warehouse status');
    const data: ApiResponse<DefaultWarehouseStatus> = await response.json();
    return data.data;
  };

export const useDefaultWarehouseStatus =
  (): UseQueryResult<DefaultWarehouseStatus, Error> => {
    return useQuery({
      queryKey: dashboardKeys.defaultWarehouseStatus(),
      queryFn: fetchDefaultWarehouseStatus,
      staleTime: 3 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    });
  };